package fr.frux.shared.domain.notification

import fr.frux.core.common.config.SiteLinks
import fr.frux.shared.content.ContentModerationPayload
import fr.frux.shared.content.HarvestReminderPayload
import fr.frux.shared.content.ModerationGround
import fr.frux.shared.content.PremiumExpiryPayload
import fr.frux.shared.content.TreeNotificationPayload
import fr.frux.shared.domain.model.NotificationKind
import fr.frux.shared.domain.model.NotificationSubjectType

/**
 * Libellé lisible d'une notification, à partir du [payload] dénormalisé côté serveur
 * (`NotificationService.toPayload`, mêmes clés). Vit dans `:shared` depuis #252 : l'écran Activité
 * et la notification système Android doivent afficher exactement le même texte.
 *
 * Pas de repli générique par kind : chaque branche connaît ses propres clés, et un payload manquant
 * retombe sur un libellé neutre plutôt que de planter sur un accès direct.
 */
object NotificationTitles {
    // table de decision a plat, une branche par kind : la lire d'un bloc est le but
    // [subjectType] ne sert qu'à `CONTENT_MODERATED` (#550), qui distingue la description d'un
    // arbre de celle d'une récolte par le sujet plutôt que par une clé de payload de plus.
    @Suppress("CyclomaticComplexMethod")
    fun of(
        kind: NotificationKind,
        payload: Map<String, String>,
        subjectType: NotificationSubjectType = NotificationSubjectType.UNKNOWN,
    ): String =
        when (kind) {
            NotificationKind.HARVEST_JOINED -> {
                val desc = payload["description"]?.takeIf { it.isNotBlank() }
                "Quelqu'un a rejoint ta récolte" + (desc?.let { " « $it »" } ?: "")
            }

            NotificationKind.HARVEST_LEFT -> {
                val desc = payload["description"]?.takeIf { it.isNotBlank() }
                "Quelqu'un a quitté ta récolte" + (desc?.let { " « $it »" } ?: "")
            }

            NotificationKind.HARVEST_FULL -> {
                val desc = payload["description"]?.takeIf { it.isNotBlank() }
                "Ta récolte est complète" + (desc?.let { " « $it »" } ?: "")
            }

            NotificationKind.HARVEST_COMPLETED -> {
                "Une récolte à laquelle tu participais est terminée"
            }

            NotificationKind.FEEDBACK_HANDLED -> {
                val date = payload["date"]?.let(::frenchDate)
                if (date !=
                    null
                ) {
                    "Ton retour du $date a été pris en compte. Merci."
                } else {
                    "Ton retour a été pris en compte. Merci."
                }
            }

            NotificationKind.HARVEST_CANCELLED -> {
                "Une récolte à laquelle tu participais a été annulée"
            }

            NotificationKind.HARVEST_STARTED -> {
                "Une récolte à laquelle tu participais a démarré"
            }

            NotificationKind.HARVEST_REMINDER -> {
                harvestReminderTitle(payload)
            }

            // La date vient du serveur, en français et en heure de Paris (#587).
            NotificationKind.PREMIUM_EXPIRING -> {
                payload[PremiumExpiryPayload.KEY_DATE]
                    ?.takeIf { it.isNotBlank() }
                    ?.let { "Ton abonnement Frux+ se termine le $it. Le digest de maturité s'arrêtera à cette date." }
                    ?: "Ton abonnement Frux+ se termine bientôt."
            }

            NotificationKind.PREMIUM_EXPIRED -> {
                "Ton abonnement Frux+ est terminé. Le digest de maturité n'est plus envoyé."
            }

            NotificationKind.TREE_DISPUTED -> {
                val species = payload["speciesCommonName"]
                "Un doute a été signalé sur ton arbre" + (species?.let { " ($it)" } ?: "")
            }

            NotificationKind.REPORT_RESOLVED -> {
                when (payload["target"]) {
                    "USER" -> "Ton signalement d'un cueilleur a été examiné. Merci."
                    "HARVEST" -> "Ton signalement d'une récolte a été examiné. Merci."
                    else -> "Ton signalement a été résolu"
                }
            }

            NotificationKind.MATURITY_DIGEST -> {
                maturityDigestTitle(payload)
            }

            // Aucun détail technique (#295) : on ne stocke ni appareil ni adresse, et un libellé
            // alarmiste sans action possible ne servirait à rien. Reconnecte-toi est la seule
            // conduite à tenir.
            NotificationKind.SESSION_REVOKED -> {
                "Un appareil a été déconnecté par sécurité. Reconnecte-toi si c'était toi."
            }

            NotificationKind.SPECIES_SUGGESTION_ACCEPTED -> {
                val name = payload["name"]?.takeIf { it.isNotBlank() }
                "Ta suggestion d'espèce" + (name?.let { " « $it »" } ?: "") + " a été acceptée"
            }

            NotificationKind.SPECIES_SUGGESTION_DISMISSED -> {
                val name = payload["name"]?.takeIf { it.isNotBlank() }
                "Ta suggestion d'espèce" + (name?.let { " « $it »" } ?: "") + " n'a pas été retenue"
            }

            NotificationKind.TREE_SPECIES_CORRECTED -> {
                val previous = payload["previousSpeciesName"].orEmpty()
                val next = payload["newSpeciesName"].orEmpty()
                "L'espèce d'un de tes arbres a été corrigée : « $previous » devient « $next »"
            }

            NotificationKind.CONTENT_MODERATED -> {
                contentModeratedTitle(payload, subjectType)
            }

            NotificationKind.TREE_ACTIVITY -> {
                treeActivityTitle(payload)
            }

            NotificationKind.FAVORITE_TREE_CHANGED -> {
                favoriteTreeChangedTitle(payload)
            }

            // Filtré en amont par `NotificationListResponse.toDomain` (#253/#157) : jamais montré,
            // ce libellé neutre n'existe que pour que `of` reste une fonction totale.
            NotificationKind.UNKNOWN -> {
                "Nouvelle activité sur ton compte"
            }
        }

    private val frenchMonths =
        listOf(
            "janvier",
            "février",
            "mars",
            "avril",
            "mai",
            "juin",
            "juillet",
            "août",
            "septembre",
            "octobre",
            "novembre",
            "décembre",
        )

    /** `yyyy-MM-dd` vers « 3 octobre » (« 1er » pour le premier), `null` si la date est illisible. */
    private fun frenchDate(iso: String): String? {
        val parts = iso.split("-")
        val month = parts.getOrNull(1)?.toIntOrNull()?.takeIf { it in 1..12 }
        val day = parts.getOrNull(2)?.toIntOrNull()?.takeIf { it in 1..31 }
        return if (parts.size != 3 || month == null || day == null) {
            null
        } else {
            (if (day == 1) "1er" else day.toString()) + " " + frenchMonths[month - 1]
        }
    }

    /**
     * « arrive en saison », jamais « est mûr » : la saison est nationale et approximative
     * (ADR 0006/0017), pas une garantie de terrain.
     */
    private fun maturityDigestTitle(payload: Map<String, String>): String {
        val count = payload["count"]?.toIntOrNull() ?: 1
        val title =
            if (count <= 1) {
                "Un de tes arbres favoris arrive en saison"
            } else {
                "$count de tes arbres favoris arrivent en saison"
            }
        // Le payload joint les libellés par virgule sans espace : réintroduite à l'affichage.
        val species =
            payload["species"]
                ?.takeIf { it.isNotBlank() }
                ?.replace(",", ", ")
        return title + (species?.let { " ($it)" } ?: "")
    }

    /** « Ton arbre (Pommier) », l'espèce entre parenthèses comme pour `TREE_DISPUTED` : un nom
     *  commun ne se prête pas toujours au possessif (« ton ail des ours »). */
    private fun speciesSuffix(payload: Map<String, String>): String =
        payload[TreeNotificationPayload.KEY_SPECIES]?.takeIf { it.isNotBlank() }?.let { " ($it)" } ?: ""

    /** Même vocabulaire que la fiche arbre (`treedetail_observation_status_long_*`), en minuscule. */
    private fun statusLabel(status: String?): String? =
        when (status) {
            "FLOWERING" -> "bientôt mûr"
            "RIPE_READY" -> "prêt à cueillir"
            "PICKED_CLEAN" -> "cueilli"
            "UNAVAILABLE" -> "indisponible"
            else -> null
        }

    /** Même vocabulaire que la fiche arbre (`treedetail_trait_*`). */
    private fun traitLabel(trait: String?): String? =
        when (trait) {
            "DELICIOUS" -> "Délicieux"
            "ACCESS_DIFFICULT" -> "Difficile d'accès"
            else -> null
        }

    /**
     * Ligne du jour sur un arbre que j'ai ajouté (#584) : le geste lui-même tant qu'il est seul, un
     * compte dès le deuxième, pour ne jamais lire comme un flux d'événements.
     */
    private fun treeActivityTitle(payload: Map<String, String>): String {
        val tree = "ton arbre" + speciesSuffix(payload)
        val count = payload[TreeNotificationPayload.KEY_COUNT]?.toIntOrNull() ?: 1
        if (count >
            1
        ) {
            return "${tree.replaceFirstChar { it.uppercase() }} a reçu $count retours de cueilleurs aujourd'hui"
        }
        val value = payload[TreeNotificationPayload.KEY_VALUE]
        return when (payload[TreeNotificationPayload.KEY_ACTIVITY]) {
            TreeNotificationPayload.ACTIVITY_STATUS -> {
                val status = statusLabel(value)
                "${tree.replaceFirstChar { it.uppercase() }} a été observé aujourd'hui" +
                    (status?.let { " : $it" } ?: "")
            }

            TreeNotificationPayload.ACTIVITY_TRAIT -> {
                val trait = traitLabel(value)
                if (trait !=
                    null
                ) {
                    "Un cueilleur a noté « $trait » sur $tree"
                } else {
                    "Un cueilleur a laissé un avis sur $tree"
                }
            }

            else -> {
                "Un cueilleur est passé par $tree"
            }
        }
    }

    /** « Récolte demain à 09:30 », avec la description entre guillemets si elle existe (#580). */
    private fun harvestReminderTitle(payload: Map<String, String>): String {
        val day =
            when (payload[HarvestReminderPayload.KEY_DAY]) {
                HarvestReminderPayload.DAY_TODAY -> " aujourd'hui"
                HarvestReminderPayload.DAY_TOMORROW -> " demain"
                else -> ""
            }
        val time = payload[HarvestReminderPayload.KEY_TIME]?.takeIf { it.isNotBlank() }?.let { " à $it" } ?: ""
        val desc = payload["description"]?.takeIf { it.isNotBlank() }?.let { " « $it »" } ?: ""
        return "Récolte$day$time$desc"
    }

    /** Dernier changement du jour sur un arbre favori (#585). */
    private fun favoriteTreeChangedTitle(payload: Map<String, String>): String {
        val species = speciesSuffix(payload)
        return when (payload[TreeNotificationPayload.KEY_CHANGE]) {
            TreeNotificationPayload.CHANGE_STATUS -> {
                val status = statusLabel(payload[TreeNotificationPayload.KEY_STATUS])
                "Du nouveau sur un de tes arbres favoris$species" + (status?.let { " : $it" } ?: "")
            }

            TreeNotificationPayload.CHANGE_GONE -> {
                "Un de tes arbres favoris$species a été signalé disparu"
            }

            TreeNotificationPayload.CHANGE_SPECIES_CORRECTED -> {
                val previous = payload[TreeNotificationPayload.KEY_PREVIOUS_SPECIES].orEmpty()
                val next = payload[TreeNotificationPayload.KEY_SPECIES].orEmpty()
                "L'espèce d'un de tes arbres favoris a été corrigée : « $previous » devient « $next »"
            }

            else -> {
                "Du nouveau sur un de tes arbres favoris$species"
            }
        }
    }

    /**
     * Modération par l'opérateur (#550, ADR 0029 amendé) : ce qui a été retiré, le fondement en
     * liste fermée et la voie de recours. Jamais le motif rédigé, qui n'est pas dans le payload.
     */
    private fun contentModeratedTitle(
        payload: Map<String, String>,
        subjectType: NotificationSubjectType,
    ): String {
        val what =
            when (payload[ContentModerationPayload.KEY_ACTION]) {
                ContentModerationPayload.ACTION_DESCRIPTION_CLEARED -> {
                    when (subjectType) {
                        NotificationSubjectType.TREE -> "La description de ton arbre a été retirée."
                        NotificationSubjectType.HARVEST -> "La description de ta récolte a été retirée."
                        else -> "Une de tes descriptions a été retirée."
                    }
                }

                ContentModerationPayload.ACTION_USERNAME_RESET -> {
                    "Ton pseudo a été remplacé par un pseudo neutre."
                }

                ContentModerationPayload.ACTION_TREE_HIDDEN -> {
                    "Ton arbre a été retiré de la carte."
                }

                ContentModerationPayload.ACTION_PHOTO_HIDDEN -> {
                    "Une de tes photos a été masquée."
                }

                else -> {
                    "Un de tes contenus a été retiré."
                }
            }
        val ground = groundLabel(payload[ContentModerationPayload.KEY_GROUND])
        val motif = ground?.let { " Motif : $it." } ?: ""
        return "$what$motif Pour contester, écris à ${SiteLinks.CONTACT_EMAIL}."
    }

    /** Libellé lisible d'un fondement, `null` pour une valeur inconnue de ce client. */
    fun groundLabel(ground: String?): String? =
        when (ground) {
            ModerationGround.OFFENSIVE -> "propos injurieux ou offensants"
            ModerationGround.PERSONAL_INFO -> "données personnelles d'un tiers"
            ModerationGround.SPAM -> "publicité ou spam"
            ModerationGround.PRIVATE_PROPERTY -> "arbre situé sur une propriété privée"
            ModerationGround.ILLEGAL -> "contenu illicite"
            ModerationGround.MISIDENTIFIED -> "espèce mal identifiée"
            ModerationGround.GONE -> "arbre disparu"
            ModerationGround.PEER_REPORTS -> "signalée par d'autres cueilleurs"
            else -> null
        }
}
